// ABOUTME: Admin dashboard component providing system administration and oversight functionality
// ABOUTME: Displays key metrics, user management, and administrative tools for system administrators

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography,
  Card,
  CardContent, 
  Button,
  Container,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  CircularProgress
} from '@mui/material';
import {
  People,
  Groups,
  Assessment,
  Security,
  ArrowBack,
  Today,
  PersonAdd,
  Timeline,
  Policy,
  Settings,
  Assignment,
  Email,
  Webhook,
  CreditCard,
  Schedule,
  TrendingUp,
  AttachMoney,
  MenuBook,
  Palette
} from '@mui/icons-material';
import { getAdminStats, getSubscriptionMetrics } from '../services/api';

interface AdminStats {
  totalUsers: number;
  totalFamilies: number;
  totalChildren: number;
  activeUsersToday: number;
  behaviorRecordsToday: number;
  newUsersThisWeek: number;
}

interface SubscriptionMetrics {
  totalSubscriptions: number;
  activeTrials: number;
  paidSubscriptionRate: number;
  monthlyRecurringRevenue: number;
}

interface SummaryBoxProps {
  icon: React.ReactNode;
  value: number;
  title: string;
  color: 'primary' | 'secondary' | 'success' | 'info' | 'warning' | 'error';
}

const SummaryBox: React.FC<SummaryBoxProps> = ({ icon, value, title, color }) => (
  <Card>
    <CardContent sx={{ textAlign: 'center' }}>
      {/* Icon glyph sizing via fontSize, not a Typography text override */}
      <Box sx={{ fontSize: 40, color: `${color}.main`, mb: 1 }}>
        {icon}
      </Box>
      <Typography variant="h4" component="div" sx={{ fontWeight: 600 }}>
        {value}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {title}
      </Typography>
    </CardContent>
  </Card>
);

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [subscriptionMetrics, setSubscriptionMetrics] = useState<SubscriptionMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const handleNavigateHome = () => {
    navigate('/');
  };

  // Fetch admin stats from API
  const fetchStats = useCallback(async () => {
    try {
      setError(null);
      const [statsData, metricsData] = await Promise.all([
        getAdminStats(),
        getSubscriptionMetrics()
      ]);
      setStats(statsData as AdminStats);
      setSubscriptionMetrics(metricsData as SubscriptionMetrics);
    } catch (err) {
      console.error('Failed to fetch admin stats:', err);
      setError('Failed to load statistics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    
    // Set up auto-refresh every 30 seconds
    const interval = setInterval(fetchStats, 30000);
    
    // Cleanup interval on component unmount
    return () => clearInterval(interval);
  }, [fetchStats]);

  const quickActions = [
    { icon: <Assessment />, title: 'Usage Analytics', description: 'Family performance analytics and CSM outreach dashboard', path: '/admin/usage-analytics' },
    { icon: <People />, title: 'User Management', description: 'View and manage user accounts', path: '/admin/users' },
    { icon: <Groups />, title: 'Family Manager', description: 'View and manage families and their members', path: '/admin/families' },
    { icon: <Groups />, title: 'Child Management', description: 'View and manage children across all families', path: '/admin/children' },
    { icon: <Assessment />, title: 'Behavior List Manager', description: 'View and manage behavior logs across all families', path: '/admin/behaviors' },
    { icon: <Email />, title: 'Email Templates', description: 'Manage system email templates and notifications', path: '/admin/email-templates' },
    { icon: <Email />, title: 'Campaign Management', description: 'Monitor and manage automated email campaigns and enrollments', path: '/admin/campaigns' },
    { icon: <MenuBook />, title: 'Knowledge Base', description: 'Manage articles, videos, categories, and tags for parenting resources', path: '/admin/knowledge-base' },
    { icon: <Policy />, title: 'Policy Management', description: 'Manage privacy policy and terms of service', path: '/admin/policies' },
    { icon: <Assignment />, title: 'Audit Log', description: 'View administrative actions and system audit trail for compliance', path: '/admin/audit' },
    { icon: <Webhook />, title: 'Webhook Log', description: 'View and retry LemonSqueezy webhook events for subscriptions', path: '/admin/webhooks' },
    { icon: <Settings />, title: 'System Settings', description: 'Configure system-wide settings and security policies', path: '/admin/settings' },
    { icon: <Security />, title: 'System Backups', description: 'Backup management and system configuration', path: '/admin/system/backups' },
    { icon: <Palette />, title: 'Theme Preview', description: 'Live preview of the MUI theme palette, typography, and component states', path: '/admin/theme-preview' }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={handleNavigateHome}
          sx={{ mr: 2 }}
        >
          Back to Dashboard
        </Button>
        <Box>
          <Typography variant="h3" component="h1" sx={{ fontWeight: 600, mb: 1 }}>
            Admin Dashboard
          </Typography>
          <Typography variant="body1" color="text.secondary">
            System administration and oversight
          </Typography>
        </Box>
      </Box>

      {/* Stats Grid */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }} data-testid="summary-stats-loading">
          <CircularProgress />
        </Box>
      ) : error ? (
        <Box sx={{ textAlign: 'center', mb: 4 }} data-testid="summary-stats-error">
          <Typography color="error">{error}</Typography>
        </Box>
      ) : (
        <Box 
          sx={{ 
            display: 'grid', 
            gridTemplateColumns: { 
              xs: 'repeat(2, 1fr)', 
              sm: 'repeat(3, 1fr)', 
              md: 'repeat(4, 1fr)', 
              lg: 'repeat(6, 1fr)' 
            },
            gap: 3,
            mb: 4 
          }} 
          data-testid="summary-stats-grid"
        >
          <SummaryBox
            icon={<People />}
            value={stats?.totalUsers || 0}
            title="Total Users"
            color="primary"
          />
          
          <SummaryBox
            icon={<Groups />}
            value={stats?.totalFamilies || 0}
            title="Total Families"
            color="secondary"
          />
          
          <SummaryBox
            icon={<Assessment />}
            value={stats?.totalChildren || 0}
            title="Total Children"
            color="info"
          />
          
          <SummaryBox
            icon={<Today />}
            value={stats?.activeUsersToday || 0}
            title="Active Users (24h)"
            color="success"
          />
          
          <SummaryBox
            icon={<Timeline />}
            value={stats?.behaviorRecordsToday || 0}
            title="Behavior Records (24h)"
            color="warning"
          />
          
          <SummaryBox
            icon={<PersonAdd />}
            value={stats?.newUsersThisWeek || 0}
            title="New Users (7d)"
            color="error"
          />

          <SummaryBox
            icon={<CreditCard />}
            value={subscriptionMetrics?.totalSubscriptions || 0}
            title="Paid Subscriptions"
            color="success"
          />

          <SummaryBox
            icon={<Schedule />}
            value={subscriptionMetrics?.activeTrials || 0}
            title="Active Trials"
            color="info"
          />

          <SummaryBox
            icon={<TrendingUp />}
            value={subscriptionMetrics?.paidSubscriptionRate || 0}
            title="Paid Rate (%)"
            color="primary"
          />

          <SummaryBox
            icon={<AttachMoney />}
            value={Math.round(subscriptionMetrics?.monthlyRecurringRevenue || 0)}
            title="MRR ($)"
            color="secondary"
          />
        </Box>
      )}

      {/* Quick Actions */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" component="h2" sx={{ mb: 3, fontWeight: 600 }}>
          Quick Actions
        </Typography>
        
        <List>
          {quickActions.map((action, index) => (
            <React.Fragment key={index}>
              <ListItem 
                component="button"
                onClick={() => action.path && navigate(action.path)}
                disabled={!action.path}
                sx={{ 
                  borderRadius: 1, 
                  mb: 1,
                  '&:hover': { backgroundColor: action.path ? 'action.hover' : 'transparent' },
                  border: 'none',
                  background: 'transparent',
                  width: '100%',
                  cursor: action.path ? 'pointer' : 'default',
                  opacity: action.path ? 1 : 0.6
                }}
              >
                <ListItemIcon sx={{ color: 'primary.main' }}>
                  {action.icon}
                </ListItemIcon>
                <ListItemText
                  primary={action.title}
                  secondary={action.description}
                  primaryTypographyProps={{ fontWeight: 600 }}
                />
              </ListItem>
              {index < quickActions.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </List>
      </Paper>

    </Container>
  );
};

export default AdminDashboard;